import React from 'react';
import '../../assets/css/case-property.css';
import cornerIcon from '../../assets/images/case-image-corner.svg';
// @ts-ignore
import { useProperty, useContentToken, EditableToken, useSlotGroup, EditableSlotGroup, useCollectionBinding, RichTextContent } from '@vibesites/runtime/react';
// @ts-ignore
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faMugHot } from '@fortawesome/free-solid-svg-icons';
// @ts-ignore
import {
  StarIcon,
  MusicIcon,
  MountainCityIcon,
  CowboyHatIcon,
  SeedlingIcon,
  CowIcon,
  LocationIcon,
} from '@vibesites/assets/icons/ui';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { PropertyContentSection } from './PropertyContentSection';
import { getPropertyBasePath } from './propertyUtils';
import { useFilteredGalleryIndices } from './useFilteredGallery';
import { NeighborhoodMapEmbed } from './NeighborhoodMapEmbed';
import { NeighborhoodPOI } from '@vibesites/schema';

const STATE_ICONS: Record<string, React.ComponentType<any>> = {
  TX: StarIcon,
  MO: MusicIcon,
  AR: MountainCityIcon,
  OK: CowboyHatIcon,
  MS: SeedlingIcon,
  KS: CowIcon,
};

// POI type values as stored in data
const POI_TYPES = {
  restaurants: 'Restaurants',
  shopping: 'Shopping & Retail',
  schools: 'Education',
  entertainment: 'Entertainment',
  health: 'Health and Wellness',
  nature: 'Nature & Outdoor Attractions',
  transit: 'Transportation & Transit',
  employers: 'Major Employers & Offices',
} as const;

interface POI {
  poi_name?: string | null;
  poi_type?: string | null;
  poi_address?: string | null;
  poi_short_description?: string | null;
  poi_miles_from_property?: string | boolean | null;
  poi_featured_image?: string | null;
  poi_business_url?: string | null;
}

function formatPoiDistance(d: string | boolean | null | undefined): string | null {
  if (typeof d !== 'string') return null;
  const trimmed = d.trim();
  if (!trimmed || trimmed.toLowerCase() === 'false') return null;
  return trimmed.toLowerCase().endsWith('mi') ? trimmed : `${trimmed} mi`;
}

function POIList({ pois, emptyMessage }: { pois: POI[]; emptyMessage: string }) {
  if (!pois.length) {
    return (
      <ul className="prop-al-checklist">
        <li><FontAwesomeIcon icon={faCircleCheck} className="prop-al-check-icon" /><span>{emptyMessage}</span></li>
      </ul>
    );
  }
  return (
    <ul className="prop-nb-poi-list">
      {pois.map((poi, i) => {
        const distance = formatPoiDistance(poi.poi_miles_from_property);
        return (
          <li key={i} className="prop-nb-poi-item">
            <FontAwesomeIcon icon={faCircleCheck} className="prop-al-check-icon" />
            <span>
              {poi.poi_name}
              {distance ? <span className="prop-nb-poi-distance"> — {distance}</span> : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// Helper to create a standard image slot definition
function createImageDef(label: string, defaultIndex: number): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${defaultIndex}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${defaultIndex}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

export const NB_SLOT_DEFS: Record<string, SlotGroupDefinition> = {
  'propertyneighborhoodmap.intro_image': createImageDef('Intro Image', 0),
  'propertyneighborhoodmap.skyline_image': createImageDef('Skyline Image', 1),
  'propertyneighborhoodmap.schools_image': createImageDef('Schools Image', 2),
  'propertyneighborhoodmap.food_bg_image': createImageDef('Food Background Image', 3),
  'propertyneighborhoodmap.shopping_image_1': createImageDef('Shopping Image 1', 4),
  'propertyneighborhoodmap.shopping_image_2': createImageDef('Shopping Image 2', 5),
  'propertyneighborhoodmap.shopping_image_3': createImageDef('Shopping Image 3', 6),
  'propertyneighborhoodmap.entertainment_image_1': createImageDef('Entertainment Image 1', 7),
  'propertyneighborhoodmap.entertainment_image_2': createImageDef('Entertainment Image 2', 8),
  'propertyneighborhoodmap.entertainment_image_3': createImageDef('Entertainment Image 3', 9),
};

export function PropertyNeighborhoodMap() {
  const property = useProperty();
  const name = property.property_name || '';
  const city = property.property_address_city || '';
  const stateAbbr = property.property_address_state || '';
  const basePath = getPropertyBasePath(property);

  // POI data — collection binding lets editors reorder / hide POIs in the CMS
  const mapPois = useCollectionBinding<NeighborhoodPOI>('propertyneighborhoodmap.map_pois', { collection: 'neighborhood_pois' });

  // POI data by type (for the content sections below the map) — derived from mapPois to respect editor overrides
  const schoolPOIs = React.useMemo(() => mapPois.filter((p) => p.poi_type === POI_TYPES.schools), [mapPois]);
  const restaurantPOIs = React.useMemo(() => mapPois.filter((p) => p.poi_type === POI_TYPES.restaurants), [mapPois]);
  const shoppingPOIs = React.useMemo(() => mapPois.filter((p) => p.poi_type === POI_TYPES.shopping), [mapPois]);
  const entertainmentPOIs = React.useMemo(() => mapPois.filter((p) => p.poi_type === POI_TYPES.entertainment), [mapPois]);

  const introTitleFallback = `Explore the Neighborhood Around ${name}`;
  const introTitle = useContentToken('propertyneighborhoodmap.intro_title') || introTitleFallback;
  const introCtaText = useContentToken('propertyneighborhoodmap.intro_cta_text') || 'View Floor Plans';

  const skylineBadgeText = useContentToken('propertyneighborhoodmap.skyline_badge_text') || `${city}, ${stateAbbr}`;


  const sectionTitle = (token: string | null, display: string) => (token || display).replace(/\{property\}/g, name);

  const fi = useFilteredGalleryIndices();
  const introImage = useSlotGroup('propertyneighborhoodmap.intro_image', createImageDef('Intro Image', fi[0] ?? 0));
  const skylineImage = useSlotGroup('propertyneighborhoodmap.skyline_image', createImageDef('Skyline Image', fi[1] ?? 1));
  const schoolsImage = useSlotGroup('propertyneighborhoodmap.schools_image', createImageDef('Schools Image', fi[2] ?? 2));
  const foodBgImage = useSlotGroup('propertyneighborhoodmap.food_bg_image', createImageDef('Food Background Image', fi[3] ?? 3));

  const shop1 = useSlotGroup('propertyneighborhoodmap.shopping_image_1', createImageDef('Shopping Image 1', fi[4] ?? 4));
  const shop2 = useSlotGroup('propertyneighborhoodmap.shopping_image_2', createImageDef('Shopping Image 2', fi[5] ?? 5));
  const shop3 = useSlotGroup('propertyneighborhoodmap.shopping_image_3', createImageDef('Shopping Image 3', fi[6] ?? 6));

  const ent1 = useSlotGroup('propertyneighborhoodmap.entertainment_image_1', createImageDef('Entertainment Image 1', fi[7] ?? 7));
  const ent2 = useSlotGroup('propertyneighborhoodmap.entertainment_image_2', createImageDef('Entertainment Image 2', fi[8] ?? 8));
  const ent3 = useSlotGroup('propertyneighborhoodmap.entertainment_image_3', createImageDef('Entertainment Image 3', fi[9] ?? 9));

  return (
    <div className="prop-nb-map-section">
      {/* Full Neighborhood Map */}
      <div className="prop-nb-map-hero">
        <NeighborhoodMapEmbed height="600px" collectionBindingPath="propertyneighborhoodmap.map_pois" />
      </div>

      {/* Intro Section */}
      <PropertyContentSection
        variant="dark"
        reverse
        left={
          <div className="prop-al-images-col" style={{ position: 'relative' }}>
            <img src={cornerIcon} alt="" className="prop-corner-icon prop-corner-icon--tl" aria-hidden="true" />
            <EditableSlotGroup groupPath="propertyneighborhoodmap.intro_image" definition={NB_SLOT_DEFS['propertyneighborhoodmap.intro_image']}>
              <div className="prop-al-single-image">
                <VibesitesImage url={introImage.image} alt={introImage.alt || ''} sizes="(min-width: 992px) 50vw, 100vw" />
              </div>
            </EditableSlotGroup>
          </div>
        }
        right={
          <div className="prop-al-text-box">
            <h1>
              <EditableToken path="propertyneighborhoodmap.intro_title" type="text" label="Intro Title" defaultValue={contentSchema.tokens['propertyneighborhoodmap.intro_title'].defaultValue}>
                {sectionTitle(introTitle, introTitleFallback)}
              </EditableToken>
            </h1>
            <div className="prop-al-separator" />
            <RichTextContent
              path="propertyneighborhoodmap.intro_blurb"
              label="Intro Description"
              defaultField={contentSchema.tokens['propertyneighborhoodmap.intro_blurb'].defaultField}
              defaultValue={contentSchema.tokens['propertyneighborhoodmap.intro_blurb'].defaultValue}
              as="div"
              className="prop-al-blurb"
            />
            <EditableToken path="propertyneighborhoodmap.intro_cta_text" type="text" label="CTA Text" as="span" style={{ display: 'contents' }} defaultValue={contentSchema.tokens['propertyneighborhoodmap.intro_cta_text'].defaultValue}>
              <a className="prop-btn" href={`${basePath}/floor-plans/`}>{introCtaText}</a>
            </EditableToken>
          </div>
        }
      />

      {/* 3. Skyline Image - bounded width */}
      <section className="prop-al-content-cols prop-al-bg-dark prop-nb-skyline-section prop-nb-skyline-bounded">
        <div className="prop-nb-full-img">
          <div style={{ position: 'relative' }}>
            <img src={cornerIcon} alt="" className="prop-corner-icon prop-corner-icon--tr" aria-hidden="true" />
            <EditableSlotGroup groupPath="propertyneighborhoodmap.skyline_image" definition={NB_SLOT_DEFS['propertyneighborhoodmap.skyline_image']}>
              <div className="prop-nb-full-img-inner">
                <VibesitesImage url={skylineImage.image} alt={skylineImage.alt || ''} />
                <div className="prop-nb-city-badge">
                  {(() => {
                    const StateIcon = STATE_ICONS[stateAbbr] || LocationIcon;
                    return <StateIcon size="1em" color="currentColor" />;
                  })()}
                  <EditableToken path="propertyneighborhoodmap.skyline_badge_text" type="text" label="Badge Text" as="span" defaultValue={contentSchema.tokens['propertyneighborhoodmap.skyline_badge_text'].defaultValue}>
                    {skylineBadgeText}
                  </EditableToken>
                </div>
              </div>
            </EditableSlotGroup>
          </div>
        </div>
      </section>

      {/* 4. Schools Section */}
      <PropertyContentSection
        variant="dark"
        left={
          <div className="prop-al-text-box" id="nb-section-schools">
            <h2>
              <RichTextContent path="propertyneighborhoodmap.schools_title" label="Schools Section Title" defaultValue={contentSchema.tokens['propertyneighborhoodmap.schools_title'].defaultValue} as="span" style={{ display: 'contents' }} />
            </h2>
            <div className="prop-al-separator" />
            <RichTextContent path="propertyneighborhoodmap.schools_blurb" label="Schools Description" defaultValue={contentSchema.tokens['propertyneighborhoodmap.schools_blurb'].defaultValue} as="div" className="prop-al-blurb" />
            <POIList pois={schoolPOIs} emptyMessage="Add nearby schools in the editor" />
          </div>
        }
        right={
          <div className="prop-al-images-col" style={{ position: 'relative' }}>
            <img src={cornerIcon} alt="" className="prop-corner-icon prop-corner-icon--tr" aria-hidden="true" />
            <EditableSlotGroup groupPath="propertyneighborhoodmap.schools_image" definition={NB_SLOT_DEFS['propertyneighborhoodmap.schools_image']}>
              <div className="prop-al-single-image">
                <VibesitesImage url={schoolsImage.image} alt={schoolsImage.alt || ''} sizes="(min-width: 992px) 50vw, 100vw" />
              </div>
            </EditableSlotGroup>
          </div>
        }
      />

      {/* 5. Food Section (Featured Text over Image) */}
      <div className="prop-nb-featured" id="nb-section-restaurants">
        <EditableSlotGroup groupPath="propertyneighborhoodmap.food_bg_image" definition={NB_SLOT_DEFS['propertyneighborhoodmap.food_bg_image']}>
          <div className="prop-nb-featured-bg">
            <VibesitesImage url={foodBgImage.image} alt={foodBgImage.alt || ''} />
          </div>
        </EditableSlotGroup>
        <div className="prop-nb-featured-overlay" />
        <div className="prop-nb-featured-content">
          <div className="prop-nb-featured-icon">
            <FontAwesomeIcon icon={faMugHot} />
          </div>
          <h2>
            <RichTextContent path="propertyneighborhoodmap.food_title" label="Food Section Title" defaultValue={contentSchema.tokens['propertyneighborhoodmap.food_title'].defaultValue} as="span" style={{ display: 'contents' }} />
          </h2>
          <RichTextContent path="propertyneighborhoodmap.food_blurb" label="Food Description" defaultValue={contentSchema.tokens['propertyneighborhoodmap.food_blurb'].defaultValue} as="div" className="prop-al-blurb" />
          <POIList pois={restaurantPOIs} emptyMessage="Add nearby restaurants and dining in the editor" />
          <a className="prop-btn" href={`${basePath}/photos/`}>View Gallery</a>
        </div>
      </div>

      {/* 6. Shopping Section */}
      <PropertyContentSection
        variant="light"
        reverse
        left={
          <div className="prop-al-images-col" style={{ width: '100%', position: 'relative' }}>
            <img src={cornerIcon} alt="" className="prop-corner-icon prop-corner-icon--tl" aria-hidden="true" />
            <div className="prop-al-photo-grid secondary" style={{ width: '100%' }}>
              <EditableSlotGroup groupPath="propertyneighborhoodmap.shopping_image_1" definition={NB_SLOT_DEFS['propertyneighborhoodmap.shopping_image_1']}>
                <div className="prop-al-photo-item shadow-lg">
                  <VibesitesImage url={shop1.image} alt={shop1.alt || ''} sizes="(min-width: 992px) 50vw, 100vw" />
                </div>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="propertyneighborhoodmap.shopping_image_2" definition={NB_SLOT_DEFS['propertyneighborhoodmap.shopping_image_2']}>
                <div className="prop-al-photo-item shadow-lg">
                  <VibesitesImage url={shop2.image} alt={shop2.alt || ''} sizes="(min-width: 992px) 25vw, 50vw" />
                </div>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="propertyneighborhoodmap.shopping_image_3" definition={NB_SLOT_DEFS['propertyneighborhoodmap.shopping_image_3']}>
                <div className="prop-al-photo-item shadow-lg">
                  <VibesitesImage url={shop3.image} alt={shop3.alt || ''} sizes="(min-width: 992px) 25vw, 50vw" />
                </div>
              </EditableSlotGroup>
            </div>
          </div>
        }
        right={
          <div className="prop-al-text-box" id="nb-section-shopping">
            <h2>
              <RichTextContent path="propertyneighborhoodmap.shopping_title" label="Shopping Section Title" defaultValue={contentSchema.tokens['propertyneighborhoodmap.shopping_title'].defaultValue} as="span" style={{ display: 'contents' }} />
            </h2>
            <div className="prop-al-separator" />
            <RichTextContent path="propertyneighborhoodmap.shopping_blurb" label="Shopping Description" defaultValue={contentSchema.tokens['propertyneighborhoodmap.shopping_blurb'].defaultValue} as="div" className="prop-al-blurb" />
            <POIList pois={shoppingPOIs} emptyMessage="Add nearby shopping in the editor" />
          </div>
        }
      />

      {/* 7. Entertainment Section */}
      <div className="prop-nb-text-center" id="nb-section-entertainment">
        <h2>
          <RichTextContent path="propertyneighborhoodmap.entertainment_title" label="Entertainment Section Title" defaultValue={contentSchema.tokens['propertyneighborhoodmap.entertainment_title'].defaultValue} as="span" style={{ display: 'contents' }} />
        </h2>
        <div className="prop-al-separator" style={{ margin: '1.5rem auto' }} />
        <RichTextContent path="propertyneighborhoodmap.entertainment_blurb" label="Entertainment Description" defaultValue={contentSchema.tokens['propertyneighborhoodmap.entertainment_blurb'].defaultValue} as="div" className="prop-al-blurb" />
        <POIList pois={entertainmentPOIs} emptyMessage="Add nearby entertainment in the editor" />
      </div>

      {/* 8. Entertainment Images Row */}
      <div className="prop-nb-image-row">
        <div className="prop-nb-image-row-inner">
          <EditableSlotGroup groupPath="propertyneighborhoodmap.entertainment_image_1" definition={NB_SLOT_DEFS['propertyneighborhoodmap.entertainment_image_1']}>
            <div className="prop-nb-img-row-item">
              <VibesitesImage url={ent1.image} alt={ent1.alt || ''} sizes="33vw" />
            </div>
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="propertyneighborhoodmap.entertainment_image_2" definition={NB_SLOT_DEFS['propertyneighborhoodmap.entertainment_image_2']}>
            <div className="prop-nb-img-row-item">
              <VibesitesImage url={ent2.image} alt={ent2.alt || ''} sizes="33vw" />
            </div>
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="propertyneighborhoodmap.entertainment_image_3" definition={NB_SLOT_DEFS['propertyneighborhoodmap.entertainment_image_3']}>
            <div className="prop-nb-img-row-item">
              <VibesitesImage url={ent3.image} alt={ent3.alt || ''} sizes="33vw" />
            </div>
          </EditableSlotGroup>
        </div>
      </div>

    </div>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyNeighborhoodMap',
  displayName: 'Property Neighborhood Full Page',
  collectionBindings: {
    'propertyneighborhoodmap.map_pois': {
      collection: 'neighborhood_pois',
      label: 'Map POIs',
    },
  },
  tokens: {
    'propertyneighborhoodmap.intro_title': { label: 'Intro Title', type: 'text', defaultValue: '', description: 'Defaults to "Explore the Neighborhood Around {property_name}"' },
    'propertyneighborhoodmap.intro_blurb': { label: 'Intro Text', type: 'richtext', defaultField: 'property_home_neighborhood_blurb', defaultValue: 'Discover the vibrant neighborhood surrounding <span data-type="field-token" data-field="property_name"></span>.' },
    'propertyneighborhoodmap.intro_cta_text': { label: 'Intro CTA Text', type: 'text', defaultValue: 'View Floor Plans' },
    'propertyneighborhoodmap.skyline_badge_text': { label: 'Skyline Badge Text', type: 'text', defaultValue: '', description: 'Defaults to "{city}, {state}"' },
    'propertyneighborhoodmap.schools_title': { label: 'Schools Section Title', type: 'richtext', defaultValue: 'Schools Near <span data-type="field-token" data-field="property_name"></span> in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>' },
    'propertyneighborhoodmap.schools_blurb': { label: 'Schools Description', type: 'richtext', defaultValue: 'Explore the educational institutions and schools located nearby.' },
    'propertyneighborhoodmap.food_title': { label: 'Restaurants & Dining Section Title', type: 'richtext', defaultValue: 'Restaurants & Dining Near <span data-type="field-token" data-field="property_name"></span> in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>' },
    'propertyneighborhoodmap.food_blurb': { label: 'Restaurants & Dining Description', type: 'richtext', defaultValue: 'Discover the restaurants and dining options located nearby.' },
    'propertyneighborhoodmap.shopping_title': { label: 'Shopping & Retail Section Title', type: 'richtext', defaultValue: 'Shopping & Retail Near <span data-type="field-token" data-field="property_name"></span> in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>' },
    'propertyneighborhoodmap.shopping_blurb': { label: 'Shopping Description', type: 'richtext', defaultValue: 'Find convenient shopping and retail options nearby.' },
    'propertyneighborhoodmap.entertainment_title': { label: 'Entertainment & Recreation Section Title', type: 'richtext', defaultValue: 'Entertainment & Recreation Near <span data-type="field-token" data-field="property_name"></span> in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>' },
    'propertyneighborhoodmap.entertainment_blurb': { label: 'Entertainment Description', type: 'richtext', defaultValue: 'Explore entertainment and recreation options nearby.' },
  },
  slotGroups: {
    'propertyneighborhoodmap.intro_image': {
      label: 'Intro Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'propertyneighborhoodmap.skyline_image': {
      label: 'Skyline Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'propertyneighborhoodmap.schools_image': {
      label: 'Schools Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'propertyneighborhoodmap.food_bg_image': {
      label: 'Food Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'propertyneighborhoodmap.shopping_image_1': {
      label: 'Shopping Image 1',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 4, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'propertyneighborhoodmap.shopping_image_2': {
      label: 'Shopping Image 2',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 5, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'propertyneighborhoodmap.shopping_image_3': {
      label: 'Shopping Image 3',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[6].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[6].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 6, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'propertyneighborhoodmap.entertainment_image_1': {
      label: 'Entertainment Image 1',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[7].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[7].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 7, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'propertyneighborhoodmap.entertainment_image_2': {
      label: 'Entertainment Image 2',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[8].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[8].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 8, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'propertyneighborhoodmap.entertainment_image_3': {
      label: 'Entertainment Image 3',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[9].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[9].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 9, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
  },
};

(PropertyNeighborhoodMap as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyNeighborhoodMap;
